"use client";

import React, { useState } from "react";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Building,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Cookies from "js-cookie";
import useApi from "@/hooks/useApi";
import { toast } from "sonner";
import API_ENDPOINTS from "@/constants/endpoints";
import { useUser } from "@/context/userContext";

type RegisterStep = "email" | "otp" | "details";

export default function Register() {
  const router = useRouter();
  const { fetchData } = useApi();
  const { setUser } = useUser();

  const [registerStep, setRegisterStep] = useState<RegisterStep>("email");
  const [registrationToken, setRegistrationToken] = useState("");
  const [otp, setOtp] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    organization: "",
    accountType: "",
    agreeToTerms: false,
    subscribeNewsletter: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateEmailStep = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateDetailsForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.username) newErrors.username = "User name is required";

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validateEmailStep()) return;

    setIsLoading(true);
    setErrors({});

    const res = await fetchData(
      API_ENDPOINTS.REGISTER_SEND_OTP,
      "POST",
      { email: formData.email.trim() },
      {},
      false,
      true
    );

    if (res !== null) {
      setRegisterStep("otp");
      setOtp("");
    }

    setIsLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.trim().length !== 6) {
      setErrors({ otp: "Enter the 6-digit verification code" });
      return;
    }

    setIsLoading(true);
    setErrors({});

    const res = await fetchData(
      API_ENDPOINTS.REGISTER_VERIFY_OTP,
      "POST",
      {
        email: formData.email.trim(),
        otp: otp.trim(),
      },
      {},
      false,
      true
    );

    if (res?.registrationToken) {
      setRegistrationToken(res.registrationToken);
      setRegisterStep("details");
    }

    setIsLoading(false);
  };

  const handleResendOtp = async () => {
    setOtp("");
    setErrors({});
    await handleSendOtp();
  };

  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateDetailsForm()) return;

    setIsLoading(true);
    setErrors({});

    const body = {
      registrationToken,
      username: formData.username.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      password: formData.password,
      organization: formData.organization || null,
      subscribe_newsletter: formData.subscribeNewsletter,
    };

    const res = await fetchData(
      API_ENDPOINTS.REGISTER_COMPLETE,
      "POST",
      body,
      {},
      false,
      false
    );

    if (res?.access_token) {
      Cookies.set("authToken", res.access_token);
      Cookies.set("plagpro_user", JSON.stringify(res.user));
      setUser(res.user);
      toast.success("Account created successfully!");
      router.push("/user");
    } else {
      toast.error("Registration failed. Please check your details.");
    }

    setIsLoading(false);
  };

  const stepTitle =
    registerStep === "email"
      ? "Create Account"
      : registerStep === "otp"
        ? "Verify Email"
        : "Complete Profile";

  const stepDescription =
    registerStep === "email"
      ? "Enter your email to receive a verification code"
      : registerStep === "otp"
        ? `Enter the 6-digit code sent to ${formData.email}`
        : "Finish setting up your PlagPro account";

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => router.push("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">PlagPro</span>
            </div>
            <CardTitle className="text-2xl">{stepTitle}</CardTitle>
            <p className="text-gray-600 mt-2">{stepDescription}</p>
          </CardHeader>

          <CardContent>
            {registerStep === "email" && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendOtp();
                }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="Enter your email"
                      className={`pl-10 ${
                        errors.email ? "border-red-500" : ""
                      } border-1 border-gray-200`}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending Code..." : "Send Verification Code"}
                </Button>
              </form>
            )}

            {registerStep === "otp" && (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="border border-gray-200 shadow-md" />
                      <InputOTPSlot index={1} className="border border-gray-200 shadow-md" />
                      <InputOTPSlot index={2} className="border border-gray-200 shadow-md" />
                      <InputOTPSlot index={3} className="border border-gray-200 shadow-md" />
                      <InputOTPSlot index={4} className="border border-gray-200 shadow-md" />
                      <InputOTPSlot index={5} className="border border-gray-200 shadow-md" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {errors.otp && (
                  <p className="text-sm text-red-600 text-center">{errors.otp}</p>
                )}

                <Button
                  type="button"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleVerifyOtp}
                  disabled={isLoading}
                >
                  {isLoading ? "Verifying..." : "Verify Email"}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-gray-600 hover:text-gray-800"
                    onClick={() => {
                      setRegisterStep("email");
                      setOtp("");
                      setErrors({});
                    }}
                  >
                    Change email
                  </button>
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-700"
                    onClick={handleResendOtp}
                    disabled={isLoading}
                  >
                    Resend code
                  </button>
                </div>
              </div>
            )}

            {registerStep === "details" && (
              <form onSubmit={handleCompleteRegistration} className="space-y-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  Verified email: <span className="font-medium">{formData.email}</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        value={formData.firstName}
                        onChange={(e) =>
                          handleInputChange("firstName", e.target.value)
                        }
                        placeholder="First name"
                        className={`pl-10 ${
                          errors.firstName ? "border-red-500" : ""
                        } border-1 border-gray-200`}
                      />
                    </div>
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      placeholder="Last name"
                      className={`${
                        errors.lastName ? "border-red-500" : ""
                      } border-1 border-gray-200`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <Input
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    placeholder="Username"
                    className={`${
                      errors.username ? "border-red-500" : ""
                    } border-1 border-gray-200`}
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                      className={`pl-10 pr-10 ${
                        errors.password ? "border-red-500" : ""
                      } border-1 border-gray-200`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      className={`pl-10 pr-10 ${
                        errors.confirmPassword ? "border-red-500" : ""
                      } border-1 border-gray-200`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization (Optional)
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      value={formData.organization}
                      onChange={(e) =>
                        handleInputChange("organization", e.target.value)
                      }
                      placeholder="University, School, or Company"
                      className="pl-10 border-1 border-gray-200"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="terms"
                      checked={formData.agreeToTerms}
                      onCheckedChange={(checked) =>
                        handleInputChange("agreeToTerms", checked as boolean)
                      }
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm text-gray-700 leading-relaxed"
                    >
                      I agree to the{" "}
                      <Link
                        href="/terms"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link
                        href="/privacy"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="text-sm text-red-600">{errors.agreeToTerms}</p>
                  )}

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="newsletter"
                      checked={formData.subscribeNewsletter}
                      onCheckedChange={(checked) =>
                        handleInputChange(
                          "subscribeNewsletter",
                          checked as boolean
                        )
                      }
                    />
                    <label htmlFor="newsletter" className="text-sm text-gray-700">
                      Subscribe to our newsletter for updates and tips (optional)
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </Button>
              </form>
            )}

            <div className="mt-6">
              <Separator className="mb-6" />
              <p className="text-center text-sm text-gray-600">
                Already have an account?{" "}
                <Link
                  href="/login"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
